import { supabase } from '@/integrations/supabase/client';
import type {
  CampaignTask,
  CreateTaskData,
  TaskStatus,
  UpdateTaskData,
} from '../types/campaign.types';

export const campaignTasksRepository = {
  async getTasks(
    campaignId: string,
    filters?: { status?: TaskStatus; assignedTo?: string }
  ): Promise<CampaignTask[]> {
    let query = supabase
      .from('campaign_tasks')
      .select(`
        id,
        title,
        description,
        status,
        priority,
        due_date,
        assigned_to,
        created_by,
        created_at,
        updated_at,
        completed_at,
        completed_by
      `)
      .eq('campaign_id', campaignId)
      .eq('is_deleted', false)
      .order('updated_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.assignedTo) {
      query = query.eq('assigned_to', filters.assignedTo);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }

    return (data || []).map((task) => ({
      ...task,
      creator_name: 'User',
      assigned_to_name: task.assigned_to ? 'Assigned User' : undefined,
      completed_by_name: task.completed_by ? 'Completed User' : undefined,
    })) as CampaignTask[];
  },

  async createTask(campaignId: string, taskData: CreateTaskData, organizationId?: string) {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      throw new Error('User not authenticated');
    }

    const insertData = {
      campaign_id: campaignId,
      organization_id: organizationId,
      title: taskData.title,
      description: taskData.description,
      status: taskData.status || 'open',
      priority: taskData.priority || 'medium',
      due_date: taskData.due_date,
      assigned_to: taskData.assigned_to,
      created_by: userData.user.id,
    };

    const { data, error } = await supabase
      .from('campaign_tasks')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error creating task:', error);
      throw error;
    }

    return data;
  },

  async updateTask(taskId: string, taskData: UpdateTaskData) {
    const { data, error } = await supabase
      .from('campaign_tasks')
      .update(taskData)
      .eq('id', taskId)
      .select()
      .single();

    if (error) {
      console.error('Error updating task:', error);
      throw error;
    }

    return data;
  },

  async deleteTask(taskId: string) {
    const { error } = await supabase
      .from('campaign_tasks')
      .update({ is_deleted: true })
      .eq('id', taskId);

    if (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },
};
